import { LoadingButton } from '@mui/lab';
import { useSegment } from 'common/hooks/segment';
import { Input } from 'components/SignInput';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createCredential } from 'services/clients/credentials';
import { QueryKeys } from '../../queries';
import { Portals } from './Portals';

interface Props {
  onClose: () => void;
}
interface FormValues {
  login: string;
  password: string;
  portalName: string;
}

export const AddCredentialForm: React.FC<Props> = (props) => {
  const { onClose } = props;
  const createMutation = useMutation(createCredential);
  const queryClient = useQueryClient();
  const { track } = useSegment();

  const { register, formState, setValue, getValues, handleSubmit, setError } = useForm<FormValues>({
    defaultValues: {
      login: '',
      password: '',
      portalName: '',
    },
    mode: 'all',
  });

  register('portalName', { required: true });

  const onSubmit = handleSubmit((values) => {
    createMutation.mutate(values, {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        // eslint-disable-next-line no-console
        console.error(error);
        setError(
          'login',
          { type: 'custom', message: error.message || 'Unknown error' },
          { shouldFocus: true }
        );

        track(
          'User added credential',
          {
            portal: values.portalName,
            method: 'toolbar-add-button',
            success: false,
            error: error.message,
          },
          { includeCompany: true, includeUser: true }
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.TerminalCredentials]);
        queryClient.invalidateQueries([QueryKeys.MissingCredentialsCount]);

        track(
          'User added credential',
          { portal: values.portalName, method: 'toolbar-add-button', success: true, error: '' },
          { includeCompany: true, includeUser: true }
        );

        onClose();
      },
    });
  });

  return (
    <form className="space-y-4" onSubmit={onSubmit} autoComplete="off">
      <div className="space-y-4">
        <h4 className="font-medium">Select a portal</h4>
        <Portals
          onSelect={(portal) => {
            setValue('portalName', portal, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          }}
        />
      </div>
      <Input
        label="Email or username*"
        type="text"
        inputProps={register('login', { required: true })}
        error={formState.errors.login}
        disabled={!getValues('portalName')}
        placeholder="Enter your email or username"
      />
      <Input
        label="Password*"
        type="password"
        inputProps={register('password', { required: true })}
        error={formState.errors.password}
        disabled={!getValues('portalName')}
        placeholder="Enter your password"
      />
      <div className="flex justify-end">
        <LoadingButton
          variant="contained"
          size="medium"
          disabled={!formState.isValid}
          loading={createMutation.isLoading}
          type="submit"
        >
          Submit credential
        </LoadingButton>
      </div>
    </form>
  );
};
